/**
 * AYP年上年下恋爱倾向测试 - 计算工具
 * 
 * 负责计算AYP指数、归一化分数、依恋类型等
 */

import { determinePersonalityType, calculateAttachmentScores, determineAttachmentType } from '@/data/personalityTypes';

/**
 * 计算原始分数
 * @param {Array} answers - 答案数组 [{dimension: 0-3, question: 0-9, rating: 1-5}]
 * @returns {Object} 原始分数 {MAT, CAR, GUI, IND}
 */
export function calculateRawScores(answers) {
  const scores = {
    MAT: 0, // 成熟偏好
    CAR: 0, // 照顾需求
    GUI: 0, // 引导欲望
    IND: 0  // 独立倾向
  };
  
  const dimensionKeys = ['MAT', 'CAR', 'GUI', 'IND'];
  
  answers.forEach(answer => {
    const dimensionKey = dimensionKeys[answer.dimension];
    scores[dimensionKey] += answer.rating; // 1-5分
  });
  
  return scores;
}

/**
 * 归一化分数 (将10-50分转换为0-100分)
 * @param {number} rawScore - 原始分数 (10-50)
 * @returns {number} 归一化分数 (0-100)
 */
export function normalizeScore(rawScore) {
  // (原始分数 - 10) * 2.5 = 归一化分数
  // 10分 -> 0分, 50分 -> 100分
  return Math.round((rawScore - 10) * 2.5);
}

/**
 * 归一化所有维度分数
 * @param {Object} rawScores - 原始分数对象
 * @returns {Object} 归一化分数对象
 */
export function normalizeAllScores(rawScores) {
  return {
    MAT: normalizeScore(rawScores.MAT),
    CAR: normalizeScore(rawScores.CAR),
    GUI: normalizeScore(rawScores.GUI),
    IND: normalizeScore(rawScores.IND)
  };
}

/**
 * 计算AYP指数
 * @param {Object} normalizedScores - 归一化后的分数 {MAT, CAR, GUI, IND}
 * @returns {number} AYP指数 (0-100)
 */
export function calculateAYP(normalizedScores) {
  const { MAT, CAR, GUI, IND } = normalizedScores;
  
  // 成熟偏好和照顾需求: 正向贡献 (越高越年上倾向)
  // 引导欲望和独立倾向: 反向贡献 (越高越年下倾向)
  const adjustedGUI = 100 - GUI;
  const adjustedIND = 100 - IND;
  
  const ayp = Math.round((MAT + CAR + adjustedGUI + adjustedIND) / 4);
  
  // 确保在0-100范围内
  return Math.max(0, Math.min(100, ayp));
}

/**
 * 完整计算流程
 * @param {Array} answers - 答案数组
 * @param {Object} userInfo - 用户信息 {nickname, gender, age, zodiac}
 * @param {string} testType - 测试类型
 * @returns {Object} 完整的测试结果
 */
export function calculateFullResult(answers, userInfo, testType) {
  // 1. 计算原始分数
  const rawScores = calculateRawScores(answers);
  
  // 2. 归一化分数
  const normalizedScores = normalizeAllScores(rawScores);
  
  // 3. 计算AYP指数
  const aypScore = calculateAYP(normalizedScores);
  
  // 4. 判定人格类型
  const personalityType = determinePersonalityType(aypScore);
  
  // 5. 计算依恋类型分数
  const attachmentScores = calculateAttachmentScores(normalizedScores);
  
  // 6. 判定依恋类型
  const attachmentType = determineAttachmentType(attachmentScores);
  
  // 7. 生成完整结果对象
  return {
    id: generateResultId(),
    timestamp: new Date().toISOString(),
    endTime: new Date().toISOString(),
    testType,
    userInfo,
    answers,
    rawScores,
    scores: normalizedScores,
    aypScore,
    personalityType,
    attachmentType: {
      primary: attachmentType.primary,
      secondary: attachmentType.secondary,
      scores: attachmentScores,
      primaryScore: attachmentType.scores.primary,
      secondaryScore: attachmentType.scores.secondary
    }
  };
}

/**
 * 生成结果ID
 * @returns {string} 唯一的结果ID
 */
function generateResultId() {
  return `ayp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 获取AYP指数的等级描述
 * @param {number} aypScore - AYP指数
 * @returns {string} 等级描述
 */
export function getAYPLevel(aypScore) {
  if (aypScore >= 81) return '极强年上倾向';
  if (aypScore >= 61) return '明显年上倾向';
  if (aypScore >= 41) return '平衡型';
  if (aypScore >= 21) return '明显年下倾向';
  return '极强年下倾向';
}

/**
 * 获取AYP指数的简短描述
 * @param {number} aypScore - AYP指数
 * @returns {string} 简短描述
 */
export function getAYPDescription(aypScore) {
  if (aypScore >= 81) {
    return '你强烈倾向于寻找年长的伴侣,享受被照顾和引导的感觉';
  } else if (aypScore >= 61) {
    return '你倾向于欣赏成熟稳重的伴侣,但也能保持一定独立性';
  } else if (aypScore >= 41) {
    return '你在年上年下倾向上保持平衡,年龄不是你选择伴侣的关键因素';
  } else if (aypScore >= 21) {
    return '你倾向于欣赏年轻有活力的伴侣,享受引导和照顾对方';
  } else {
    return '你强烈倾向于寻找年轻的伴侣,享受引导和照顾对方的感觉';
  }
}

/**
 * 获取维度的百分比描述
 * @param {number} score - 维度分数 (0-100)
 * @returns {string} 百分比描述
 */
export function getDimensionLevel(score) {
  if (score >= 80) return '非常高';
  if (score >= 60) return '较高';
  if (score >= 40) return '中等';
  if (score >= 20) return '较低';
  return '非常低';
}

/**
 * 验证答案完整性
 * @param {Array} answers - 答案数组
 * @returns {boolean} 是否完整
 */
export function validateAnswers(answers) {
  // 应该有40道题的答案
  if (answers.length !== 40) {
    return false;
  }
  
  // 检查每个答案是否有效
  return answers.every(answer => {
    return (
      typeof answer.dimension === 'number' &&
      typeof answer.question === 'number' &&
      typeof answer.rating === 'number' &&
      answer.dimension >= 0 && answer.dimension <= 3 &&
      answer.question >= 0 && answer.question <= 9 &&
      answer.rating >= 1 && answer.rating <= 5
    );
  });
}

/**
 * 计算测试进度百分比
 * @param {number} currentDimension - 当前维度 (0-3)
 * @param {number} currentQuestion - 当前题目 (0-9)
 * @returns {number} 进度百分比 (0-100)
 */
export function calculateProgress(currentDimension, currentQuestion) {
  const totalQuestions = 40;
  const completedQuestions = currentDimension * 10 + currentQuestion;
  return Math.round((completedQuestions / totalQuestions) * 100);
}

/**
 * 格式化测试时长
 * @param {Date} startTime - 开始时间
 * @param {Date} endTime - 结束时间
 * @returns {string} 格式化的时长
 */
export function formatDuration(startTime, endTime) {
  const duration = Math.floor((endTime - startTime) / 1000); // 秒
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  
  if (minutes > 0) {
    return `${minutes}分${seconds}秒`;
  }
  return `${seconds}秒`;
}

/**
 * 获取维度雷达图数据
 * @param {Object} normalizedScores - 归一化分数
 * @returns {Array} 雷达图数据
 */
export function getRadarChartData(normalizedScores) {
  return [
    { dimension: '成熟偏好', value: normalizedScores.MAT, fullMark: 100 },
    { dimension: '照顾需求', value: normalizedScores.CAR, fullMark: 100 },
    { dimension: '引导欲望', value: normalizedScores.GUI, fullMark: 100 },
    { dimension: '独立倾向', value: normalizedScores.IND, fullMark: 100 }
  ];
}

/**
 * 获取依恋类型雷达图数据
 * @param {Object} attachmentScores - 依恋类型分数
 * @returns {Array} 雷达图数据
 */
export function getAttachmentRadarData(attachmentScores) {
  return [
    { type: '安全-自主', value: Math.round(attachmentScores['安全-自主平衡型']), fullMark: 100 },
    { type: '探索-依赖', value: Math.round(attachmentScores['探索-依赖型']), fullMark: 100 },
    { type: '独立-回避', value: Math.round(attachmentScores['独立-回避型']), fullMark: 100 },
    { type: '矛盾-动态', value: Math.round(attachmentScores['矛盾-动态型']), fullMark: 100 }
  ];
}

export default {
  calculateRawScores,
  normalizeScore,
  normalizeAllScores,
  calculateAYP,
  calculateFullResult,
  getAYPLevel,
  getAYPDescription,
  getDimensionLevel,
  validateAnswers,
  calculateProgress,
  formatDuration,
  getRadarChartData,
  getAttachmentRadarData
};

